// Which show this console is inside, read once for the frame.
//
// THE DIRECTORY IS NOT A REPORT. GET /v1/activations writes no report_access
// row and hands over no attendee. ReportNav still reads nothing: this file is
// the one read, so opening Dashboard / Registration / Kiosk / Data does not
// prefetch those reports just to print a name. Show details is the write of
// the same row this file already holds.
//
// A DEEP LINK AND AN OPEN FROM THE LIST BOTH FETCH. Reusing Choose's list
// looked cheap and leaked that list into the next mount of this console.

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import type { ActivationDirectory, ActivationLine } from "../../../operator/src/activations.js";
import { readActivations, type ApiOptions, type Failure } from "../api/client.js";
import type { SessionToken } from "../session/store.js";
import { activationName } from "./activationName.js";

export type ShowState =
  | { readonly status: "idle"; readonly reload: () => void }
  | { readonly status: "reading"; readonly reload: () => void }
  | {
      readonly status: "ready";
      readonly directory: ActivationDirectory;
      readonly line: ActivationLine | null;
      readonly reload: () => void;
    }
  | { readonly status: "failed"; readonly failure: Failure; readonly reload: () => void };

const noop = (): void => {};

const ShowContext = createContext<ShowState>({ status: "idle", reload: noop });

export function useShow(): ShowState {
  return useContext(ShowContext);
}

export function showHeading(
  state: ShowState,
): { readonly text: string; readonly unnamed: boolean } | null {
  if (state.status !== "ready" || state.line === null) return null;
  return activationName(state.line.name);
}

export type ShowReaderProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string | null;
  readonly onSessionEnded: (detail: string) => void;
  readonly children: ReactNode;
};

export function ShowReader({
  api,
  token,
  activationId,
  onSessionEnded,
  children,
}: ShowReaderProps): ReactNode {
  const [generation, setGeneration] = useState(0);
  const reload = useCallback(() => {
    setGeneration((n) => n + 1);
  }, []);

  const [state, setState] = useState<ShowState>({ status: activationId === null ? "idle" : "reading", reload });

  useEffect(() => {
    if (activationId === null) {
      setState({ status: "idle", reload });
      return;
    }

    let cancelled = false;
    setState((previous) => (previous.status === "ready" ? previous : { status: "reading", reload }));

    void (async () => {
      const result = await readActivations<ActivationDirectory>(api, token);
      if (cancelled) return;
      if (!result.ok) {
        setState({ status: "failed", failure: result, reload });
        if (result.kind === "session_ended") onSessionEnded(result.detail);
        return;
      }
      setState(ready(result.value, activationId, reload));
    })();

    return () => {
      cancelled = true;
    };
  }, [api, token, activationId, onSessionEnded, generation, reload]);

  return <ShowContext.Provider value={state}>{children}</ShowContext.Provider>;
}

function ready(directory: ActivationDirectory, activationId: string, reload: () => void): ShowState {
  return {
    status: "ready",
    directory,
    line: (directory.activations ?? []).find((row) => row.activation_id === activationId) ?? null,
    reload,
  };
}
